import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { Link, Stack, Typography } from '@mui/material';
import { isAllowedLegalRefUrl } from '@prospero/shared';
import { useTranslation } from 'react-i18next';

export type LegalReferenceAudience = 'tenant' | 'consultant';

interface LegalReferenceLinkProps {
  catalogKey: string | null;
  legalRefUrl: string | null;
  /** Falso per i tipi senza durata predefinita (Altro, DVR): non c'è nessun valore da verificare. */
  showNotice?: boolean;
  audience?: LegalReferenceAudience;
}

const NOTICE_KEY: Record<LegalReferenceAudience, string> = {
  tenant: 'indicativeValueNotice',
  consultant: 'indicativeValueNoticeConsultant',
};

function readSourceHost(legalRefUrl: string | null): string {
  if (legalRefUrl === null) {
    return '';
  }
  try {
    return new URL(legalRefUrl).hostname;
  } catch {
    return '';
  }
}

export function LegalReferenceLink({
  catalogKey,
  legalRefUrl,
  showNotice = true,
  audience = 'tenant',
}: LegalReferenceLinkProps) {
  const { t } = useTranslation('documentTypes');
  const catalogLabel = catalogKey === null ? '' : t(`${catalogKey}.legalRef`, { defaultValue: '' });
  const label = catalogLabel === '' ? readSourceHost(legalRefUrl) : catalogLabel;
  if (label === '') {
    return null;
  }
  return (
    <Stack spacing={0.25}>
      {legalRefUrl === null || !isAllowedLegalRefUrl(legalRefUrl) ? (
        <Typography variant="body2">{label}</Typography>
      ) : (
        <Link
          href={legalRefUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t('openSourceAria', { label })}
          variant="body2"
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minHeight: 24 }}
        >
          {label}
          <OpenInNewOutlinedIcon fontSize="inherit" aria-hidden />
        </Link>
      )}
      {showNotice && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {t(NOTICE_KEY[audience])}
        </Typography>
      )}
    </Stack>
  );
}
